/**
 * Barcode utility library for scanner app
 *
 * Provides common functions for parsing and working with barcodes used throughout the application.
 */

import { SCANNER_CONSTANTS, VALIDATION_CONSTANTS } from '../constants/scanner';

/**
 * Parsed barcode data
 */
export interface ParsedBarcode {
  productCode: string;
  batchNumber: string;
  quantity: number;
}

/**
 * Parse a product barcode
 *
 * Supports two formats:
 * 1. Full format: "PRODUCTCODE#BATCHNUMBER#QUANTITY"
 * 2. Simple format: "PRODUCTCODE" (defaults to batch="" and quantity=1)
 *
 * @param barcode - The barcode string to parse
 * @returns Parsed barcode data, or null if invalid format
 *
 * @example
 * parseProductBarcode("ABC123#BATCH001#5.5") // { productCode: "ABC123", batchNumber: "BATCH001", quantity: 5.5 }
 * parseProductBarcode("ABC123") // { productCode: "ABC123", batchNumber: "", quantity: 1 }
 * parseProductBarcode("INVALID##") // null
 */
export function parseProductBarcode(barcode: string): ParsedBarcode | null {
  if (!barcode || barcode.trim().length === 0) {
    return null;
  }

  const parts = barcode.split(SCANNER_CONSTANTS.BARCODE_DELIMITER);

  // Full format: CODE#BATCH#QUANTITY
  if (parts.length === 3) {
    try {
      const productCode = parts[0].trim();
      const batchNumber = parts[1].trim();
      let quantity: number;

      // Handle both comma and dot as decimal separator
      if (parts[2].includes(",")) {
        quantity = parseFloat(parts[2].replace(",", "."));
      } else {
        quantity = parseFloat(parts[2]);
      }

      // Validate parsed data
      if (!productCode || isNaN(quantity) || quantity < VALIDATION_CONSTANTS.MIN_QUANTITY) {
        return null;
      }

      return { productCode, batchNumber, quantity };
    } catch {
      return null;
    }
  }

  // Simple format: just product code
  if (parts.length === 1) {
    const productCode = barcode.trim();
    if (!productCode) {
      return null;
    }
    return { productCode, batchNumber: "", quantity: 1 };
  }

  return null;
}

/**
 * Check if a barcode represents a position/location marker
 *
 * Position codes are identified by ending with "@" character
 *
 * @param code - The code to check
 * @returns True if the code is a position marker
 *
 * @example
 * isPositionCode("A1-01@") // true
 * isPositionCode("ABC123") // false
 */
export function isPositionCode(code: string): boolean {
  if (!code) return false;
  return code.trim().endsWith(SCANNER_CONSTANTS.POSITION_MARKER);
}

/**
 * Extract the position identifier from a position code
 *
 * Removes the "@" suffix and converts to uppercase
 *
 * @param code - The position code to parse
 * @returns The position identifier, or empty string if invalid
 *
 * @example
 * extractPosition("A1-01@") // "A1-01"
 * extractPosition("b2-05@") // "B2-05"
 * extractPosition("ABC123") // "ABC123" (no @ found, returns as-is)
 */
export function extractPosition(code: string): string {
  if (!code) return "";
  const regex = new RegExp(`${SCANNER_CONSTANTS.POSITION_MARKER}$`);
  return code.trim().replace(regex, "").toUpperCase();
}

/**
 * Format a barcode for display
 *
 * @param parsed - The parsed barcode data
 * @returns A human-readable string representation
 *
 * @example
 * formatBarcodeForDisplay({ productCode: "ABC123", batchNumber: "BATCH001", quantity: 5.5 })
 * // "ABC123 (šarža: BATCH001), 5.5"
 */
export function formatBarcodeForDisplay(parsed: ParsedBarcode, unit?: string): string {
  let result = parsed.productCode;

  if (parsed.batchNumber) {
    result += ` (šarža: ${parsed.batchNumber})`;
  }

  result += `, ${parsed.quantity}`;

  if (unit) {
    result += ` ${unit}`;
  }

  return result;
}

/**
 * Create a barcode string from components
 *
 * @param productCode - Product code
 * @param batchNumber - Batch number (optional)
 * @param quantity - Quantity (optional, defaults to 1)
 * @returns Formatted barcode string
 *
 * @example
 * createBarcode("ABC123", "BATCH001", 5.5) // "ABC123#BATCH001#5.5"
 * createBarcode("ABC123") // "ABC123"
 */
export function createBarcode(productCode: string, batchNumber?: string, quantity?: number): string {
  if (!productCode) {
    throw new Error("Product code is required");
  }

  const delimiter = SCANNER_CONSTANTS.BARCODE_DELIMITER;

  if (batchNumber && quantity !== undefined) {
    return `${productCode}${delimiter}${batchNumber}${delimiter}${quantity}`;
  }

  if (batchNumber) {
    return `${productCode}${delimiter}${batchNumber}${delimiter}1`;
  }

  return productCode;
}

/**
 * Create a position barcode
 *
 * @param position - Position identifier
 * @returns Position barcode with @ suffix
 *
 * @example
 * createPositionCode("A1-01") // "A1-01@"
 */
export function createPositionCode(position: string): string {
  if (!position) {
    throw new Error("Position is required");
  }

  const cleaned = position.trim().toUpperCase();
  if (cleaned.endsWith(SCANNER_CONSTANTS.POSITION_MARKER)) {
    return cleaned;
  }

  return `${cleaned}${SCANNER_CONSTANTS.POSITION_MARKER}`;
}

/**
 * Validate a product code format
 *
 * @param productCode - The product code to validate
 * @returns True if valid
 */
export function isValidProductCode(productCode: string): boolean {
  if (!productCode || productCode.trim().length === 0) {
    return false;
  }

  // Product codes should not contain special characters used in barcode format
  return !VALIDATION_CONSTANTS.INVALID_PRODUCT_CODE_CHARS.some(char => productCode.includes(char));
}

/**
 * Normalize quantity value (handle both comma and dot decimal separators)
 *
 * @param value - String representation of quantity
 * @returns Parsed number or null if invalid
 *
 * @example
 * normalizeQuantity("5,5") // 5.5
 * normalizeQuantity("5.5") // 5.5
 * normalizeQuantity("abc") // null
 */
export function normalizeQuantity(value: string): number | null {
  if (!value) return null;

  try {
    const normalized = value.replace(",", ".");
    const parsed = parseFloat(normalized);

    if (isNaN(parsed) || parsed < VALIDATION_CONSTANTS.MIN_QUANTITY || parsed > VALIDATION_CONSTANTS.MAX_QUANTITY) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}
